import cx from 'classnames'
import Styles from './ActivityEvent.module.scss'
import { BaseActivityEvent } from './BaseActivityEvent'
import { Actor } from './Actor'
import {
  ArticleInformalFeedbackRequestClosedEvent,
  BaseActivityEventProps,
  ArticleInformalFeedbackRequestClosedEventReason,
  ActivityEventContext,
} from './Types'
import { InformalFeedbackRequestAction } from './InformalFeedbackRequestAction'
import { InformalFeedbackReviewState } from 'api/clients/InformalFeedbackRequestsClient'
import { ProseDisplay } from '../../common/ProseInput/ProseDisplay'
import { Approval } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { ReviewSummaryModal } from 'components/ReviewSummaryModal/ReviewSummaryModal'
import { useModals } from 'almanac-ui/Modal'
import { EventIconContainer } from './EventIconContainer'

interface Props extends Partial<BaseActivityEventProps> {
  event: ArticleInformalFeedbackRequestClosedEvent
  context?: ActivityEventContext
}

const ReviewSummaryButton = ({ event }: Props) => {
  const { openModal } = useModals()
  const requestId = event.data.request.id

  return (
    <Button size="sm" variant="tertiary" onPress={() => openModal(ReviewSummaryModal, { requestId })}>
      Review Summary
    </Button>
  )
}

const ArticleInformalFeedbackRequestClosed = ({ event, ...props }: Props) => {
  const {
    user,
    data: {
      message,
      reviews,
      reason,
      request: { id, key },
      branch,
    },
  } = event
  const { actions, className, context, ...rest } = props
  const { openModal } = useModals()
  if (branch) {
    return null
  }
  const nonDismissedReviewsCount = reviews.filter(({ state }) => state !== InformalFeedbackReviewState.Dismissed).length
  const activeReviewsCount = nonDismissedReviewsCount || reviews.length

  let content = null
  switch (reason) {
    case ArticleInformalFeedbackRequestClosedEventReason.Expired:
      content = `Review auto-ended on due date`
      break
    case ArticleInformalFeedbackRequestClosedEventReason.AllReviewersApproved:
      content = (
        <>
          {activeReviewsCount === 1 ? (
            <Actor
              displayName={reviews[0].reviewer.display_name || 'Anonymous User'}
              userId={reviews[0]?.reviewer?.id}
            />
          ) : (
            <>All {activeReviewsCount} reviewers</>
          )}{' '}
          approved the review, which automatically ended
        </>
      )
      break
    case ArticleInformalFeedbackRequestClosedEventReason.AllReviewersDismissed:
      content = (
        <>
          {activeReviewsCount === 1 ? (
            <Actor
              displayName={reviews[0].reviewer.display_name || 'Anonymous User'}
              userId={reviews[0]?.reviewer?.id}
            />
          ) : (
            <>All {activeReviewsCount} reviewers</>
          )}{' '}
          skipped the review, which automatically ended
        </>
      )
      break
    case ArticleInformalFeedbackRequestClosedEventReason.UserEnded:
      content = (
        <>
          <Actor displayName={user.display_name || 'Anonymous User'} userId={user?.id} /> manually ended the review
        </>
      )
      break
    case ArticleInformalFeedbackRequestClosedEventReason.DecisionMade:
      content = (
        <>
          <Actor displayName={user.display_name || 'Anonymous User'} userId={user?.id} /> ended review #{key} with a
          decision
        </>
      )
      break
    default:
      content = (
        <>
          <Actor displayName={user.display_name || 'Anonymous User'} userId={user?.id} /> approved the review, which
          automatically ended
        </>
      )
      break
  }

  const handleClick = (e: MouseEvent) => {
    openModal(ReviewSummaryModal, { requestId: id })
  }

  return (
    <>
      <BaseActivityEvent
        {...rest}
        onClick={handleClick}
        className={cx(className, Styles.informalFeedbackRequest, Styles.closed)}
        icon={
          <EventIconContainer>
            <Approval size={16} />
          </EventIconContainer>
        }
        timestamp={event.timestamp}
        message={message && <ProseDisplay html={message} />}
        content={content}
        buttons={context === 'review' ? null : <ReviewSummaryButton event={event} />}
        actions={
          <>
            <InformalFeedbackRequestAction event={event} />
            {actions}
          </>
        }
      />
    </>
  )
}

export { ArticleInformalFeedbackRequestClosed }
